import React, { useCallback, useEffect, useState } from 'react';
import { App, Button, Drawer, Empty } from 'antd';

import DialogLoadState from '../common/DialogLoadState';
import { Skeleton } from '../loading';
import UserAvatar from '../users/UserAvatar';
import { userLabel } from '../users/UserChip';
import { exportConfirmers, listConfirmers } from '../../services/confirmations';
import { downloadBlob } from '../../services/exports';
import { apiErrorMessage } from '../../utils/apiErrors';
import { formatDisplayDateTime } from '../../utils/dateDisplay';
import { formatRelativeToNow } from '../../utils/formatters';
import { anchorOf } from '../../platform';

const PAGE = 50;

/**
 * WHO said "me too" on an item (KPR-390) — the collection's managers' panel,
 * opened from the read view's "See who". Newest first, a page at a time
 * (`GET …/confirmations/{element}/people?skip&limit`); each row is the
 * person's avatar, their name and when. Never an email: the API serves none.
 * A person whose account has gone reads "A deleted account" (the API's
 * `user: null`).
 *
 * A panel, not a window (docs/platform-standards.md §11): the list beside the
 * item, which stays readable, closes with Esc or ✕.
 *
 * **Download** (KPR-440, ruled a03) takes the same people away as a CSV file —
 * name and the day each confirmed, oldest first, nothing else — for a
 * petition handed on or a list kept elsewhere. The panel's one action, in its
 * header, offered once the list has said somebody confirmed. The file is made
 * on request by the API and never stored; the API notes in the item's History
 * that a manager took it. Past 100,000 people the file holds the first that
 * many, and a notice says so.
 */
export default function ConfirmersDrawer({ open, onClose, itemId, element, label }) {
	const [people, setPeople] = useState([]);
	const [total, setTotal] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [downloading, setDownloading] = useState(false);
	const { message } = App.useApp();

	const load = useCallback(async (skip) => {
		setLoading(true);
		setError(null);
		try {
			const page = await listConfirmers(itemId, element, { skip, limit: PAGE });
			setPeople(prev => (skip ? [...prev, ...page.people] : page.people));
			setTotal(page.total);
		} catch (err) {
			setError(apiErrorMessage(err, 'The list could not be loaded. Please try again.'));
		} finally {
			setLoading(false);
		}
	}, [itemId, element]);

	useEffect(() => {
		if (!open || !itemId || !element) return;
		setPeople([]);
		setTotal(null);
		load(0);
	}, [open, itemId, element, load]);

	const more = total !== null && people.length < total;

	const download = useCallback(async (event) => {
		const anchor = anchorOf(event);
		setDownloading(true);
		try {
			const file = await exportConfirmers(itemId, element, { label });
			await downloadBlob(file.blob, file.filename, { anchor });
			if (file.truncated) {
				message.warning(`The file holds the first ${Number(file.rowCount || 0).toLocaleString()} people of ${Number(file.totalCount || 0).toLocaleString()}.`);
			}
		} catch (err) {
			message.error(apiErrorMessage(err, 'The file could not be made. Please try again.'));
		} finally {
			setDownloading(false);
		}
	}, [itemId, element, label, message]);

	return (
		<Drawer
			className="confirmers"
			title={`Who said “${label || 'Me too'}”`}
			open={open}
			onClose={onClose}
			placement="right"
			width="min(420px, 100vw)"
			destroyOnClose
			extra={total ? (
				<Button className="confirmers__download" onClick={download} loading={downloading}>Download</Button>
			) : null}
		>
			<DialogLoadState
				loaded={total !== null}
				loading={loading}
				error={Boolean(error)}
				errorDetail={error || undefined}
				label="this list"
				onRetry={() => load(0)}
				skeleton={<Skeleton variant="list-row" count={4} />}
				minHeight={200}
			>
			{total === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nobody yet." /> : null}
			{people.length ? (
				<ul className="confirmers__list" aria-label={`People who said “${label || 'Me too'}”`}>
					{people.map((entry, index) => {
						const user = entry?.user || null;
						const name = user ? userLabel(user) : 'A deleted account';
						return (
							<li className="confirmers__row" key={user?._id || `gone-${index}`}>
								<UserAvatar user={user || {}} size={32} />
								<span className="confirmers__who">
									<span className="confirmers__name">{name}</span>
									{entry?.confirmedAt ? (
										<span className="confirmers__when" title={formatDisplayDateTime(entry.confirmedAt)}>
											{formatRelativeToNow(entry.confirmedAt)}
										</span>
									) : null}
								</span>
							</li>
						);
					})}
				</ul>
			) : null}
			{more ? (
				<Button className="confirmers__more" onClick={() => load(people.length)} loading={loading}>Show more</Button>
			) : null}
			</DialogLoadState>
		</Drawer>
	);
}
